import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

import { PackageType } from '@assetaccountant/api/models/package-type';
import { ListOrganisationPermissionsResponse } from '../contract/authorization/list-organisation-permissions.response';
import { ListOrganisationRegisterRolesResponse } from '../contract/authorization/list-organisation-register-roles.response';
import { ListOrganisationRegisterUsersResponse } from '../contract/authorization/list-organisation-register-users.response';
import { ListOrganisationRolesResponse } from '../contract/authorization/list-organisation-roles.response';
import { ListOrganisationUsersResponse } from '../contract/authorization/list-organisation-users.response';
import { ORGANISATION_PLACEHOLDER } from '../http-token.interceptor';
import { CreateTagResponse } from '../models/create-tag-response';
import { ListRegistersResponse } from '../models/list-registers-response';
import { ListTagsResponse } from '../models/list-tags-response';
import { OrganisationUser } from '../models/organisation-user';
import { Tag } from '../models/tag';
import { UpdateTagResponse } from '../models/update-tag-response';
import { CreateRegisterCommand } from '../operations/create-register-command';
import { CreateRegisterResponse } from '../operations/create-register-response';

@Injectable({
  providedIn: 'root',
})
export class OrganisationService {
  constructor(private http: HttpClient) {}

  recordAccess(organisationId: number) {
    const url = `${environment.servicesApiUrl}/api/v2/organisations/${organisationId}/access`;

    return this.http.get(url);
  }

  setBillingCountry(billingCountryId: number) {
    return this.http.put(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/set-billing-country`,
      { billingCountryId },
    );
  }

  createRegister(
    register: CreateRegisterCommand,
    organisationId?: number,
  ): Observable<CreateRegisterResponse> {
    return this.http.post<CreateRegisterResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/register`,
      register,
    );
  }

  listRegisters(organisationId?: number) {
    return this.http.get<ListRegistersResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/registers`,
    );
  }

  listRoles(organisationId?: number) {
    return this.http.get<ListOrganisationRolesResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/roles`,
    );
  }

  listRegisterRoles(organisationId?: number) {
    return this.http.get<ListOrganisationRegisterRolesResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/registerRoles`,
    );
  }

  removeUser(organisationId: number, userIdToRemove: string) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/users/${userIdToRemove}`,
    );
  }

  removeSelf(organisationId: number) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/users`,
    );
  }

  listUsers({
    organisationId,
    includeInvited,
  }: { organisationId?: number; includeInvited?: boolean } = {}) {
    const url = `${environment.servicesApiUrl}/api/v2/organisations/${
      organisationId || ORGANISATION_PLACEHOLDER
    }/users`;
    let params = new HttpParams();
    if (includeInvited) {
      params = params.append('includeInvited', includeInvited.toString());
    }
    return this.http.get<ListOrganisationUsersResponse>(url, { params });
  }

  listRegisterUsers({
    organisationId,
    registerId,
  }: {
    organisationId?: number;
    registerId: number;
  }) {
    return this.http.get<ListOrganisationRegisterUsersResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId || ORGANISATION_PLACEHOLDER
      }/registers/${registerId}/users`,
    );
  }

  getUser({ organisationId, userId }: { organisationId?: number; userId: string }) {
    return this.http.get<OrganisationUser>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId || ORGANISATION_PLACEHOLDER
      }/users/${userId}`,
    );
  }

  updateUser(organisationId: number, email: string, organisationRoleId: number) {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/user`,
      { email, organisationRoleId },
    );
  }

  updateUsers(organisationId: number, emails: string[], organisationRoleId: number) {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId ? organisationId : ORGANISATION_PLACEHOLDER
      }/users`,
      { emails, organisationRoleId },
    );
  }

  // This is not supported, You cannot remove user by updating the organisationRoleId to -1
  // removeUsers(organisationId: number, emails: string[]) {
  //   return this.http.post(
  //     `${environment.servicesApiUrl}/api/v2/organisations/${
  //       organisationId ? organisationId : ORGANISATION_PLACEHOLDER
  //     }/users`,
  //     { emails, organisationRoleId: -1 },
  //   );
  // }

  listTags({
    organisationId,
    includeDisabled,
  }: { organisationId?: number; includeDisabled?: boolean } = {}) {
    const url = `${environment.servicesApiUrl}/api/v2/organisations/${
      organisationId || ORGANISATION_PLACEHOLDER
    }/tags`;
    let params = new HttpParams();
    if (includeDisabled) {
      params = params.append('includeDisabled', includeDisabled.toString());
    }
    return this.http.get<ListTagsResponse>(url, { params });
  }

  updateTag(organisationId: number, tag: Tag): Observable<UpdateTagResponse> {
    return this.http.put<UpdateTagResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId || ORGANISATION_PLACEHOLDER
      }/tags/${tag.id}`,
      { tag },
    );
  }

  createTag(organisationId: number, tag: Tag): Observable<CreateTagResponse> {
    return this.http.post<CreateTagResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId || ORGANISATION_PLACEHOLDER
      }/tags`,
      { tag },
    );
  }

  saveTag(organisationId: number, tag: Tag) {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId || ORGANISATION_PLACEHOLDER
      }/tags`,
      { tag },
    );
  }

  uploadLogo(form: FormData) {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/logo`,
      form,
      { responseType: 'blob' },
    );
  }

  getLogo(organisationId?: number) {
    return this.http.get(
      `${environment.servicesApiUrl}/api/v2/organisations/${
        organisationId || ORGANISATION_PLACEHOLDER
      }/logo`,
      { responseType: 'blob' },
    );
  }

  createSubscription(organisationId, packageType: PackageType) {
    return this.http.post<null>(
      `${environment.servicesApiUrl}/api/v2/organisations/${organisationId}/subscription`,
      { PackageType: packageType },
    );
  }

  createPaymentCustomer(organisationId) {
    return this.http.post<null>(
      `${environment.servicesApiUrl}/api/v2/organisations/${organisationId}/payment-customer`,
      {},
    );
  }

  updateSubscription(organisationId, packageType: PackageType) {
    return this.http.put<null>(
      `${environment.servicesApiUrl}/api/v2/organisations/${organisationId}/subscription`,
      { PackageType: packageType },
    );
  }

  deleteOrganisationRole(roleId: number) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/organisation-role/${roleId}`,
    );
  }

  deleteRegisterRole(roleId: number) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/register-role/${roleId}`,
    );
  }

  deleteRegister(registerId: number) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/registers/${registerId}`,
    );
  }

  listPermissions() {
    return this.http.get<ListOrganisationPermissionsResponse>(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/permissions`,
    );
  }

  transferOwnership() {
    return this.http.get(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/transfer-ownership`,
    );
  }

  updateRegisterUser(userId, request) {
    return this.http.put(
      `${environment.servicesApiUrl}/api/v2/organisations/${ORGANISATION_PLACEHOLDER}/registers/${request.registerId}/users/${userId}`,
      request,
    );
  }
}
